import {
  Button,
  Cushion,
  FlexContainer,
  Grid,
  Rectangle,
  Typography,
  withTheme,
} from '@pitchero/react-ui';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Icon from '../icon';
import useBreakpoint from '../../lib/use-breakpoint';

const MonthFilter = ({ allowFuture, earliestMonth, onChange, selected, uiTheme }) => {
  const isMobile = useBreakpoint();
  const now = DateTime.local();
  const current = selected ? DateTime.fromFormat(`${selected}-01`, 'yyyy-MM-dd') : now;
  const [year, setYear] = useState(selected ? current.year : now.year);
  const primaryColor = uiTheme.colors.primary;
  const earliestDate = earliestMonth
    ? DateTime.fromISO(earliestMonth)
    : DateTime.fromObject({ year: 2008, month: 2, day: 1 });
  const allowFutureYears = allowFuture ? false : year >= now.year;

  const months = [];
  for (let month = 1; month <= 12; month += 1) {
    months.push(DateTime.fromObject({ year, month, day: 1 }));
  }

  const listStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: 0,
    padding: 0,
    [`@media screen and (max-width: 767px)`]: {
      display: 'block',
      listStylePosition: 'outside',
      listStyleType: 'none',
    },
  };

  const listItemStyle = {
    listStyleType: 'none',
    [`@media screen and (max-width: 767px)`]: {
      color: primaryColor,
      listStyleType: 'disc',
    },
  };

  const renderYearSelect = () => (
    <FlexContainer
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      component="li"
    >
      <Button
        onClick={() => setYear(year - 1)}
        disabled={year <= earliestDate.year}
        theme="text"
        type="button"
        tabIndex={0}
      >
        <Icon
          name="arrow-left"
          preset="name"
          color={year <= earliestDate.year ? 'dustygrey' : 'primary'}
        />
      </Button>
      <Cushion horizontal="small">
        <Typography color="primary" preset="name">
          {year}
        </Typography>
      </Cushion>
      <Button
        onClick={() => setYear(year + 1)}
        disabled={allowFutureYears}
        theme="text"
        type="button"
        tabIndex={0}
      >
        <Icon name="arrow-right" preset="name" color={allowFutureYears ? 'dustygrey' : 'primary'} />
      </Button>
    </FlexContainer>
  );

  const renderMonthSelect = (month) => {
    const allowFutureMonths = allowFuture ? false : month > now;
    return (
      <Cushion
        key={month.toFormat('y-MM-dd')}
        vertical="small"
        responsive={[{ minWidth: 'tab', props: { vertical: '0' } }]}
        component="li"
        style={month.month === current.month ? listItemStyle : { listStyleType: 'none' }}
      >
        <Button
          tabIndex={0}
          onClick={() => onChange(month)}
          disabled={allowFutureMonths}
          theme="text"
          type="button"
        >
          <Typography
            preset="subtitle--small"
            color={month.month === current.month ? 'black' : 'dustygrey'}
          >
            {isMobile ? month.toFormat('LLL') : month.toFormat('LLLL')}
          </Typography>
        </Button>
      </Cushion>
    );
  };

  if (isMobile) {
    return (
      <FlexContainer flexDirection="column" alignItems="center">
        <Cushion bottom="small">{renderYearSelect()}</Cushion>
        <Grid columns="repeat(4, 1fr)" rows="repeat(3, 1fr)" columnGap="large">
          {months.map((month) => renderMonthSelect(month))}
        </Grid>
        {selected && (
          <Cushion top="small">
            <button type="button" onClick={() => onChange(null)}>
              <Typography preset="name" color="primary">
                Clear Filter
              </Typography>
            </button>
          </Cushion>
        )}
      </FlexContainer>
    );
  }

  return (
    <Rectangle style={listStyle} component="ol">
      <Cushion responsive={[{ maxWidth: 767, props: { bottom: 'small' } }]}>
        {renderYearSelect()}
      </Cushion>
      {months.map((month) => renderMonthSelect(month))}
    </Rectangle>
  );
};

MonthFilter.defaultProps = {
  allowFuture: null,
  earliestMonth: null,
  selected: null,
};

MonthFilter.propTypes = {
  allowFuture: PropTypes.bool,
  earliestMonth: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.string,
  uiTheme: PropTypes.shape().isRequired,
};

export default withTheme(MonthFilter);
